import React, { useEffect, useMemo, useState } from 'react';
import './ProfileBanner.css';
import PlayButton from '../components/PlayButton';
import MoreInfoButton from '../components/MoreInfoButton';
import { getProfileBanner } from '../queries/getProfileBanner';
import { ProfileBanner as ProfileBannerType } from '../types';

type ProfileBannerProps = {
  profile: ProfileBannerType['profileName'];
};

const fallbackHeadline: Record<ProfileBannerType['profileName'], string> = {
  recruiter: 'Recruiter View',
  developer: 'Developer View',
  stalker: 'Stalker View',
  adventure: 'Adventurer View',
};

const ProfileBanner: React.FC<ProfileBannerProps> = ({ profile }) => {
  const [bannerData, setBannerData] = useState<ProfileBannerType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        const data = await getProfileBanner(profile);
        if (isMounted) {
          setBannerData(data);
          setError(null);
        }
      } catch {
        if (isMounted) {
          setError('Unable to load profile banner right now.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [profile]);

  const media = useMemo(() => {
    if (!bannerData) return null;

    return {
      imageUrl: bannerData.backgroundImage?.url?.trim() || '',
      videoUrl: bannerData.backgroundVideo?.url?.trim() || '',
    };
  }, [bannerData]);

  if (isLoading) {
    return (
      <div className="profile-banner is-loading" aria-busy="true">
        <div className="banner-content banner-skeleton">
          <div className="skeleton-line skeleton-title" />
          <div className="skeleton-line skeleton-body" />
          <div className="skeleton-line skeleton-body short" />
          <div className="banner-buttons">
            <div className="skeleton-button" />
            <div className="skeleton-button" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !bannerData) {
    return (
      <div className="profile-banner profile-banner--fallback">
        <div className="banner-content">
          <h1 className="banner-headline">{fallbackHeadline[profile]}</h1>
          <p className="banner-description">
            {error || 'Profile details are unavailable at the moment.'}
          </p>
        </div>
      </div>
    );
  }

  const handlePlayClick = () => {
    if (bannerData.resumeLink?.url) {
      window.open(bannerData.resumeLink.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLinkedinClick = () => {
    if (bannerData.linkedinLink) {
      window.open(bannerData.linkedinLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="profile-banner">
      {media?.videoUrl ? (
        <video
          className="profile-banner-media"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={media.imageUrl || undefined}
        >
          <source src={media.videoUrl} />
        </video>
      ) : media?.imageUrl ? (
        <div
          className="profile-banner-media profile-banner-media--image"
          style={{ backgroundImage: `url(${media.imageUrl})` }}
        />
      ) : null}

      <div className="banner-content">
        <h1 className="banner-headline" id="headline">
          {bannerData.headline}
        </h1>
        <p className="banner-description">
          {bannerData.profileSummary}
        </p>

        <div className="banner-buttons">
          <PlayButton onClick={handlePlayClick} label="Resume" />
          <MoreInfoButton onClick={handleLinkedinClick} label="Linkedin" />
        </div>
      </div>
    </div>
  );
};

export default ProfileBanner;
